import { useState, type JSX } from 'react';
import type { Agent, AgentAsset } from '@netdeck/contracts';
import type { TFunction } from 'i18next';
import { useTranslation } from '../../shared/i18n';
import { messageText } from '../../shared/i18n/message';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { downloadAgentAsset, useAgentRelease, useTestTcp } from './api';

export interface InstallPanelProps {
  agent: Agent;
  /** Одноразовый токен. Показывается один раз, сразу после выпуска. */
  enrollmentToken: string | null;
  onRotateToken: () => void;
  rotating: boolean;
}

/**
 * Панель установки агента — правая колонка макета, раздел 08.
 *
 * Готовая команда с подставленным адресом платформы и токеном: администратору
 * площадки не нужно ничего собирать вручную, а значит негде и ошибиться.
 *
 * Файлы качаются отсюда же. Раньше команда начиналась с `netdeck-agent`
 * и молча предполагала, что бинарник на машине уже лежит, — а откуда он там
 * возьмётся, интерфейс не говорил нигде. Теперь сборки под все платформы
 * и скрипты установки печёт сам образ платформы и раздаёт их по кнопке:
 * площадке в закрытом сегменте, кроме платформы, ходить всё равно некуда.
 */
export function InstallPanel({
  agent,
  enrollmentToken,
  onRotateToken,
  rotating,
}: InstallPanelProps): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const release = useAgentRelease();

  const builds = (release.data?.assets ?? []).filter((asset) => asset.kind === 'agent');

  /*
    Выбранная платформа — общая и для скачивания, и для команды: файл,
    который скачали, и файл, который названа запустить команда, обязаны
    совпадать, иначе установка спотыкается на первом же шаге.

    Пока каталог не загрузился (или пуст), выбор равен `linux/amd64`:
    команду показать всё равно нужно, а имена файлов в ней тогда общие.
  */
  const [chosen, setChosen] = useState<string | null>(null);

  const platform = chosen ?? builds[0]?.id ?? 'linux/amd64';
  const build = builds.find((asset) => asset.id === platform);
  const windows = platform.startsWith('windows');
  const script = (release.data?.assets ?? []).find(
    (asset) => asset.id === `script/${windows ? 'windows' : 'linux'}`,
  );

  const [downloading, setDownloading] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const grab = (asset: string): void => {
    setDownloading(asset);
    setFailure(null);

    downloadAgentAsset(asset)
      .catch((error: unknown) => {
        setFailure(errorText(error));
      })
      .finally(() => {
        setDownloading(null);
      });
  };

  return (
    /*
      `min-h-0` и своя прокрутка — не украшение разметки. Панель ложится
      второй строкой сетки, когда колонки рядом не помещаются, и без права
      ужаться она забирала всю высоту себе: список агентов сжимался в ноль
      и на узком экране пропадал целиком.
    */
    <div className="flex min-h-0 flex-col overflow-auto">
      <header className="flex h-11 flex-none items-center border-b border-line bg-raised px-3.5 text-base font-semibold">
        {t('agents.install.title', { name: agent.name })}
      </header>

      <div className="flex flex-col gap-3 p-3.5">
        <p className="text-sm leading-[1.5] text-muted">{t('agents.install.intro')}</p>

        {enrollmentToken ? (
          <Labeled title={t('agents.install.tokenTitle')}>
            <code className="block rounded-sm border border-line-strong bg-canvas px-2.5 py-2.5 font-mono text-sm break-all text-fg select-all">
              {enrollmentToken}
            </code>
          </Labeled>
        ) : (
          <div className="flex flex-col gap-2 rounded-sm border border-line bg-canvas px-2.5 py-2.5">
            <p className="text-sm text-muted">
              {agent.awaitingEnrollment
                ? t('agents.install.tokenShownOnce')
                : t('agents.install.alreadyEnrolled')}
            </p>
            <Button
              icon="key"
              size="sm"
              variant="ghost"
              onClick={onRotateToken}
              disabled={rotating}
            >
              {rotating ? t('agents.install.issuing') : t('agents.install.issueToken')}
            </Button>
            <p className="font-mono text-xs text-dim">{t('agents.install.rotateNote')}</p>
          </div>
        )}

        <Labeled title={t('agents.install.download.title')}>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-1">
              {(builds.length > 0 ? builds : PLACEHOLDER_PLATFORMS).map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setChosen(option.id)}
                  aria-pressed={option.id === platform}
                  className={`rounded-sm border px-2 py-1 text-xs ${
                    option.id === platform
                      ? 'border-line-strong bg-control text-fg'
                      : 'border-transparent text-dim hover:text-fg'
                  }`}
                >
                  {platformName(option.id, t)}
                </button>
              ))}
            </div>

            {build === undefined ? (
              <p className="text-xs leading-[1.5] text-warn">
                {t('agents.install.download.unavailable')}
              </p>
            ) : (
              <>
                <Button
                  icon="download"
                  size="sm"
                  disabled={downloading !== null}
                  onClick={() => {
                    grab(build.id);
                  }}
                >
                  {downloading === build.id
                    ? t('agents.install.download.working')
                    : t('agents.install.download.agent', {
                        size: build.sizeBytes,
                        version: release.data?.version ?? t('common.dash'),
                      })}
                </Button>

                {script === undefined ? null : (
                  <Button
                    icon="download"
                    size="sm"
                    variant="ghost"
                    disabled={downloading !== null}
                    onClick={() => {
                      grab(script.id);
                    }}
                  >
                    {downloading === script.id
                      ? t('agents.install.download.working')
                      : t('agents.install.download.script')}
                  </Button>
                )}

                {/*
                  Сумма показывается рядом, а не прячется: скачанный
                  исполняемый файл ставят службой от root, и единственный
                  способ убедиться, что доехало ровно то, что выложили, —
                  сверить её на месте. Команда сверки подписана тут же:
                  без неё сумма остаётся строкой, которую некуда деть.
                */}
                <div className="flex flex-col gap-1">
                  <span className="font-mono text-2xs break-all text-dim select-all">
                    {build.sha256}
                  </span>
                  <code className="block font-mono text-2xs break-all whitespace-pre-wrap text-faint">
                    {/*
                      Через `echo`, а не через `<<<`: строка-здесь есть
                      в bash, но не в `sh`, а команду копируют куда придётся.

                      Перенос по любому месту (`break-all`): в строке лежит
                      сумма в 64 знака без единого пробела — на телефоне она
                      уезжала за край экрана вместе с хвостом команды.
                      Пробелы при этом сохраняются (`whitespace-pre-wrap`):
                      между суммой и именем файла их два, и `sha256sum -c`
                      разбирает строку именно по ним.
                    */}
                    {windows
                      ? `Get-FileHash ${build.file}`
                      : `echo "${build.sha256}  ${build.file}" | sha256sum -c -`}
                  </code>
                </div>
              </>
            )}

            {failure === null ? null : <p className="text-xs text-down-fg">{failure}</p>}
          </div>
        </Labeled>

        <Labeled title={t('agents.install.commandTitle', { platform: platformName(platform, t) })}>
          <pre className="overflow-x-auto rounded-sm border border-line bg-canvas px-2.5 py-2.5 font-mono text-xs leading-[1.6] whitespace-pre-wrap text-muted">
            {installCommand(platform, enrollmentToken, build, script, t)}
          </pre>
        </Labeled>

        <Button
          icon="copy"
          size="sm"
          onClick={() =>
            void navigator.clipboard?.writeText(
              installCommand(platform, enrollmentToken, build, script, t),
            )
          }
        >
          {t('agents.install.copyCommand')}
        </Button>

        <TunnelCheck agent={agent} />
      </div>
    </div>
  );
}

/** Проверка туннеля — нижний блок правой колонки макета. */
function TunnelCheck({ agent }: { agent: Agent }): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [host, setHost] = useState('');
  const [port, setPort] = useState('5900');
  const test = useTestTcp();

  const online = agent.status === 'online';

  return (
    <div className="flex flex-col gap-2 border-t border-line-subtle pt-3">
      <div className="font-mono text-2xs tracking-[0.08em] text-faint uppercase">
        {t('agents.probe.title')}
      </div>

      <div className="flex gap-1.5">
        <input
          value={host}
          onChange={(event) => setHost(event.target.value)}
          placeholder="10.20.0.11"
          aria-label={t('agents.probe.host')}
          disabled={!online}
          className="h-7 min-w-0 flex-1 rounded-sm border border-line-strong bg-raised px-2 font-mono text-sm text-fg placeholder:text-faint disabled:opacity-50"
        />
        <input
          value={port}
          onChange={(event) => setPort(event.target.value)}
          aria-label={t('agents.probe.port')}
          disabled={!online}
          className="h-7 w-[74px] rounded-sm border border-line-strong bg-raised px-2 font-mono text-sm text-fg disabled:opacity-50"
        />
        <Button
          icon="plug"
          size="sm"
          variant="ghost"
          disabled={!online || !host.trim() || test.isPending}
          onClick={() =>
            test.mutate({ agentId: agent.id, host: host.trim(), port: Number(port) || 0 })
          }
        >
          {test.isPending ? t('agents.probe.testing') : t('agents.probe.test')}
        </Button>
      </div>

      <div className="font-mono text-xs">
        {!online ? (
          <span className="text-dim">{t('agents.probe.offline')}</span>
        ) : test.isPending ? (
          <span className="text-dim">{t('agents.probe.connecting')}</span>
        ) : test.error ? (
          <span className="text-down-fg">{errorText(test.error)}</span>
        ) : test.data ? (
          <span className={test.data.ok ? 'text-up' : 'text-down-fg'}>
            {test.data.ok
              ? t('agents.probe.ok', { ms: test.data.elapsedMs })
              : messageText(test.data.error ?? '', t)}
          </span>
        ) : (
          <span className="text-dim">{t('agents.probe.hint')}</span>
        )}
      </div>
    </div>
  );
}

function Labeled({
  title,
  action,
  children,
}: {
  title: string;
  action?: JSX.Element;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-2xs tracking-[0.08em] text-faint uppercase">{title}</span>
        {action}
      </div>
      {children}
    </div>
  );
}

/**
 * Платформы, пока каталог сборок не ответил или пуст.
 *
 * Выбор из них ничего не скачивает, но команду установки показывает: она
 * нужна и тому, кто взял бинарник не отсюда.
 */
const PLACEHOLDER_PLATFORMS = [
  { id: 'linux/amd64' },
  { id: 'linux/arm64' },
  { id: 'windows/amd64' },
];

/**
 * Название платформы для человека.
 *
 * Незнакомый ключ показывается как есть: каталог сборок может собрать кто
 * угодно подо что угодно, и молча прятать такую строку хуже, чем показать
 * `freebsd/amd64`.
 */
function platformName(id: string, t: TFunction): string {
  return t(`agents.install.download.platform.${id}`, { defaultValue: id });
}

/**
 * Команда установки с подставленным адресом платформы и именами скачанных
 * файлов.
 *
 * Адрес берётся из текущего origin, а не из настроек: администратор открыл
 * интерфейс именно по тому адресу, по которому агент и должен подключаться.
 *
 * Имена файлов — те же, что у кнопок выше: в них есть версия, и команда
 * с общим `netdeck-agent` спотыкалась бы о `netdeck-agent-0.1.0-linux-amd64`
 * сразу же.
 */
function installCommand(
  platform: string,
  token: string | null,
  build: AgentAsset | undefined,
  script: AgentAsset | undefined,
  t: TFunction,
): string {
  const server = window.location.origin;
  const value = token ?? t('agents.install.tokenPlaceholder');
  const windows = platform.startsWith('windows');

  const binary = build?.file ?? (windows ? 'netdeck-agent.exe' : 'netdeck-agent');
  const installer = script?.file ?? (windows ? 'install-windows.ps1' : 'install-linux.sh');

  if (!windows) {
    return [
      `chmod +x ${binary}`,
      `sudo sh ${installer} \\`,
      `  --binary ./${binary} \\`,
      `  --server ${server} \\`,
      `  --token ${value}`,
    ].join('\n');
  }

  return [
    `powershell -ExecutionPolicy Bypass -File .\\${installer} \``,
    `  -Binary .\\${binary} \``,
    `  -Server ${server} \``,
    `  -Token ${value}`,
  ].join('\n');
}
